import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Customer } from './cust-details-modal.interface';
import { EfoMatchingService } from 'src/app/shared/services/matching.service';
import * as _ from 'lodash';
import * as moment from 'moment';
import { RobomailService } from 'src/app/shared/services/robomail.service';
import { EfoFacebookService } from 'src/app/shared/services/facebook.service';

@Component({
  selector: 'cust-details-modal',
  templateUrl: './cust-details-modal.component.html',
  styleUrls: ['./cust-details-modal.component.css'],
})
export class CustDetailsModalComponent implements OnInit {
  @Input() customer: Customer;
  @Input() activeTab: 'sales' | 'svc' | 'vin' | 'all' | 'timeline' = 'all';

  @Output() close = new EventEmitter();

  dmsRecordsSales: any = [];
  dmsRecordsSvc: any = [];
  dmsRecordsActive: any = [];
  vinRecords: Map<string, any> = new Map();
  isExcluded: boolean[] = [false];
  loading = [];
  amountOfRecordsTxt: string = '';
  marketingDate: any = '';
  grossRevenue: number = 0;
  selectedVin: string = '';
  vins: any = [];
  tabLoading: boolean = false;
  timelineData: any = [];
  timelineLoaded: boolean = false;

  constructor(
    private efoMatching: EfoMatchingService,
    private robomail: RobomailService,
    private facebook: EfoFacebookService,
  ) {}

  async ngOnInit(): Promise<void> {
    if (this.customer) {
      if (!Array.isArray(this.customer.cust_num)) {
        this.customer.cust_num = [this.customer.cust_num];
      }
      await this.loadData();
    }
  }

  async ngOnChanges(changes): Promise<void> {
    if (changes.customer && !changes.customer.firstChange && this.customer) {
      await this.loadData();
    }
  }

  handleClose() {
    this.close.emit();
  }

  async loadData() {
    this.tabLoading = true;
    const promises = [
      this.efoMatching
        .getDmsSalesByCustNum(
          this.customer.account_num,
          this.customer.cust_num,
          this.customer.campaign_key,
        )
        .toPromise(),
      this.efoMatching
        .getDmsServiceByCustNum(
          this.customer.account_num,
          this.customer.cust_num,
          this.customer.campaign_key,
        )
        .toPromise(),
      this.checkForExludedCustNum(),
    ];

    [this.dmsRecordsSales, this.dmsRecordsSvc] = await Promise.all(promises);
    this.dmsRecordsSvc = this.dmsRecordsSvc.map((r) => {
      return { ...r, type: 'svc' };
    });
    this.dmsRecordsSales = this.dmsRecordsSales.map((r) => {
      return { ...r, type: 'sales' };
    });
    this.marketingDate = this.dmsRecordsSales.concat(
      this.dmsRecordsSvc,
    )[0]?.marketing_date;
    if (this.marketingDate != null) {
      this.marketingDate = moment(this.marketingDate).format('YYYY-MM-DD');
    }

    await this.handleTabChange(this.activeTab);
    this.tabLoading = false;
  }

  async handleTabChange(event: any) {
    this.activeTab = event;
    if (event == 'all') {
      this.dmsRecordsActive = this.dmsRecordsSales
        .concat(this.dmsRecordsSvc)
        .sort((a, b) => {
          return (
            new Date(b.deal_book_date ?? b.close_date).getTime() -
            new Date(a.deal_book_date ?? a.close_date).getTime()
          );
        });
      this.amountOfRecordsTxt = `${this.dmsRecordsActive.length} Sale and Service Records Found`;
    } else if (event == 'sales') {
      this.dmsRecordsActive = this.dmsRecordsSales;
      this.amountOfRecordsTxt = `${this.dmsRecordsActive.length} Sale Records Found`;
    } else if (event == 'svc') {
      this.dmsRecordsActive = this.dmsRecordsSvc;
      this.amountOfRecordsTxt = `${this.dmsRecordsActive.length} Service Records Found`;
    } else if (event == 'vin') {
      const allRecords = this.dmsRecordsSales.concat(this.dmsRecordsSvc);
      this.vins = _.uniq(allRecords.map((r) => r.vin));
      this.vins = _.uniq(
        this.vins.concat(allRecords.map((r) => r.vins).flat()),
      ).filter((v) => v != null);
      this.selectedVin = this.vins[0];
      this.handleVinChange(this.selectedVin);
    } else if (event == 'timeline') {
      await this.formTimeline();
    }
    this.calculateGrossRevenue();
  }

  async checkForExludedCustNum() {
    const excludedCustNums = await this.efoMatching
      .getExcludedCustNums(this.customer.account_num)
      .toPromise();
    let i = 0;
    this.isExcluded = [];
    for (const custNum of this.customer.cust_num) {
      if (excludedCustNums) {
        this.isExcluded.push(excludedCustNums.includes(custNum));
      } else this.isExcluded.push(false);
      i++;
    }
  }

  async handleExcludeCustNum(idx) {
    this.isExcluded[idx] = !this.isExcluded[idx];
    this.loading.push(idx);
    await this.efoMatching
      .saveExcludedCustNums(
        this.customer.account_num,
        this.customer.cust_num[idx],
        this.isExcluded[idx],
      )
      .toPromise();
    this.loading.findIndex((l) => l == idx) > -1 &&
      this.loading.splice(
        this.loading.findIndex((l) => l == idx),
        1,
      );
  }

  async handleVinChange(event: any) {
    this.tabLoading = true;

    if (!this.vinRecords.has(event)) {
      const promises = [
        this.efoMatching
          .getDmsSalesByVin(this.customer.account_num, event)
          .toPromise(),
        this.efoMatching
          .getDmsServiceByVin(this.customer.account_num, event)
          .toPromise(),
      ];
      let [sales, svc] = await Promise.all(promises);
      svc = svc.map((r) => {
        return { ...r, type: 'svc' };
      });
      sales = sales.map((r) => {
        return { ...r, type: 'sales' };
      });
      this.vinRecords.set(
        event,
        sales.concat(svc).sort((a, b) => {
          return (
            new Date(b.deal_book_date ?? b.close_date).getTime() -
            new Date(a.deal_book_date ?? a.close_date).getTime()
          );
        }),
      );
    }
    this.dmsRecordsActive = this.vinRecords.get(event);

    this.amountOfRecordsTxt = `${this.dmsRecordsActive.length} Records Found for Vin`;
    this.calculateGrossRevenue();
    this.tabLoading = false;
  }

  getEmailUrl(email_creative) {
    const cid = email_creative.cid;
    let m = email_creative.creative_num;
    if (+m < 10) {
      m = `0${m}`;
    }

    return `https://www.myvipdealer.com/viewmessage.php?eui=[EUI]&cid=${cid}&m=${m}`;
  }

  async formTimeline() {
    if (this.timelineLoaded) return;
    this.tabLoading = true;
    this.timelineData = [];
    if (!!this.marketingDate) {
      const promises = [
        this.robomail
          .getEmailBodyFilesByAccount(
            this.customer.account_num,
            this.marketingDate,
          )
          .toPromise(),
        this.facebook
          .getAdsByAccountAndDate(this.customer.account_num, this.marketingDate)
          .toPromise(),
      ];
      const [robomailData, facebookData] = await Promise.all(promises);

      this.timelineData.push(
        ...facebookData.map((f) => {
          return {
            time: f?.data.created_time?.split('T')[0],
            body: [
              {
                tag: 'h1',
                content: 'Social Media',
                attr: {
                  style:
                    'background-color: #ff774c; border-radius: 5px; padding-left: 5px;',
                },
              },
              {
                tag: 'p',
                content: `${f?.data.created_time?.split('T')[0]}`,
              },
              {
                tag: 'div',
                content: 'Loading Creative...',
                attr: {
                  style: 'width:500px; height: 200px; border:none',
                  class: 'iframe-placeholder',
                  'data-src': f?.data.previews.data[0].body
                    .split('"')[1]
                    .replace('&amp;', '&'),
                  'data-style': 'width:500px; height: 200px; border:none',
                },
              },
            ],
          };
        }),
      );
      this.timelineData.push(
        ...robomailData.map((r) => {
          return {
            time: r?.creative_date?.split('T')[0],
            body: [
              {
                tag: 'h1',
                content: `Email ${
                  r.creative_type == 'sales'
                    ? '- Sales'
                    : r.creative_type == 'svc'
                    ? '- Service'
                    : ''
                }`,
                attr: {
                  style:
                    'background-color: #ff774c; border-radius: 5px; padding-left: 5px;',
                },
              },
              {
                tag: 'p',
                content: `${r?.creative_date?.split('T')[0]}`,
              },
              {
                tag: 'div',
                content: 'Loading Creative...',
                attr: {
                  style: 'width: 350px; height: 200px; border:none',
                  class: 'iframe-placeholder',
                  'data-src': this.getEmailUrl(r),
                  'data-width': '350px',
                  'data-height': '200px',
                },
              },
            ],
          };
        }),
      );

      this.timelineData.push({
        time: this.marketingDate,
        body: [
          {
            tag: 'h1',
            content: 'Edifice Starts Marketing',
            attr: {
              style:
                'background-color: #ff774c; border-radius: 5px; padding-left: 5px;',
            },
          },
          {
            tag: 'p',
            content: `${this.marketingDate}<br/>Added to campaign: ${this.customer.campaign_key}.`,
            attr: {
              style: 'height=270px;',
            },
          },
        ],
      });
    }
    this.timelineData.push(
      ...this.dmsRecordsSales.concat(this.dmsRecordsSvc).map((r) => {
        const style =
          r.type == 'sales'
            ? 'background-color: rgb(255, 244, 250); border-radius: 5px; padding-left: 5px;'
            : r.type == 'svc'
            ? 'background-color: rgb(245, 249, 255); border-radius: 5px; padding-left: 5px;'
            : '';
        const type =
          r.type == 'sales' ? 'Sale' : r.type === 'svc' ? 'Service' : '';
        const content = `${
          (r.close_date ?? r.deal_book_date)?.split('T')[0]
        }<br/>${r.year} ${r.make} ${r.model} <br/>Vin: ${r.vin} <br/>Cost: $${
          r.roamount ?? r.total_of_payments
        } ${
          r.type == 'svc'
            ? ` <br/>Op Code(s): ${r.ops_code}<br/>Op Description(s): ${r.ops_description}`
            : ''
        }`;
        return {
          time: (r.deal_book_date ?? r.close_date)?.split('T')[0],

          body: [
            {
              tag: 'h1',
              content: `${type} - ${r.ronumber ?? r.deal_num}`,
              attr: {
                style: style,
              },
            },
            {
              tag: 'p',
              content: content,
              attr: {
                style: 'min-height: 270px;',
              },
            },
          ],
        };
      }),
    );
    this.timelineData = this.timelineData.filter((r) => !!r.time);
    this.tabLoading = false;
    this.timelineLoaded = true;
  }

  calculateGrossRevenue() {
    const cutoffDate = new Date(this.marketingDate);
    this.grossRevenue = this.dmsRecordsActive.reduce((acc, curr) => {
      if (
        (new Date(curr.deal_book_date) > cutoffDate ||
          new Date(curr.close_date) > cutoffDate) &&
        this.marketingDate != ''
      )
        acc += +(curr.roamount ?? curr.total_of_payments);
      return acc;
    }, 0);
  }
}
